import React, { ReactElement, useEffect } from 'react';

import { isEmpty } from '@utils/common';
import { addToDate } from '../../helpers/dateHelper';

import { Task } from '@interfaces/ganttTasks/ganttTasks';

import { MIN_GANTT_HEIGHT } from '@components/common/Gantt/constants';

import styles from './GridBody.module.scss';

export type GridBodyProps = {
	tasks: Task[];
	dates: Date[];
	svgWidth: number;
	rowHeight: number;
	columnWidth: number;
	todayColor: string;
	setTodayColumnScrollX: (scrollX: number) => void;
};

const GridBody: React.FC<GridBodyProps> = ({
	tasks,
	dates,
	rowHeight,
	svgWidth,
	columnWidth,
	todayColor,
	setTodayColumnScrollX,
}) => {
	const isEmptyData = isEmpty(tasks);

	let y = isEmptyData ? MIN_GANTT_HEIGHT : 0;
	const gridRows: ReactElement[] = [];

	for (const task of tasks) {
		gridRows.push(
			<rect
				key={'Row' + task.id}
				className={styles.gridRow}
				height={rowHeight}
				width={svgWidth}
				x='0'
				y={y}
			/>,
		);

		y += rowHeight;
	}

	const now = new Date();
	let tickX = 0;
	const ticks: ReactElement[] = [];
	let today: ReactElement = <rect />;
	let todayScrollX: number | null = null;

	for (let i = 0; i < dates.length; i++) {
		const date = dates[i];

		ticks.push(
			<line
				key={date.getTime()}
				className={styles.gridTick}
				x1={tickX}
				x2={tickX}
				y1={0}
				y2={y}
			/>,
		);

		if (
			(i + 1 !== dates.length &&
				date.getTime() < now.getTime() &&
				dates[i + 1].getTime() >= now.getTime()) ||
			// if current date is last
			(i !== 0 &&
				i + 1 === dates.length &&
				date.getTime() < now.getTime() &&
				addToDate(
					date,
					date.getTime() - dates[i - 1].getTime(),
					'millisecond',
				).getTime() >= now.getTime())
		) {
			today = (
				<rect
					fill={todayColor}
					height={isEmptyData ? MIN_GANTT_HEIGHT : y}
					width={columnWidth}
					x={tickX}
					y={0}
				/>
			);

			todayScrollX = columnWidth * (i - 1);
		}

		tickX += columnWidth;
	}

	useEffect(() => {
		if (todayScrollX !== null) {
			setTodayColumnScrollX(todayScrollX);
		}
	}, [todayScrollX, setTodayColumnScrollX]);

	return (
		<g className='gridBody'>
			<g className='rows'>
				{isEmptyData
					? <rect
						className={styles.gridRow}
						height={MIN_GANTT_HEIGHT}
						width={svgWidth}
						x='0'
						y={MIN_GANTT_HEIGHT}
					/>
					: gridRows
				}
			</g>
			<g className='ticks'>{ticks}</g>
			<g className='today'>{today}</g>
		</g>
	);
};

export default GridBody;
